// Helpers shared by the free SVG tools (/svg-viewer, /svg-to-react, /svg-to-data-uri).
// They work on text the visitor pasted or dropped, in the browser; nothing is sent anywhere.
// Parsing uses the browser's DOMParser, which builds an inert document: scripts in it never
// run and the resources it names are never fetched.

export const SVG_NS = 'http://www.w3.org/2000/svg';
export const XLINK_NS = 'http://www.w3.org/1999/xlink';
export const XML_NS = 'http://www.w3.org/XML/1998/namespace';
export const XMLNS_NS = 'http://www.w3.org/2000/xmlns/';
export const XHTML_NS = 'http://www.w3.org/1999/xhtml';
export const MATHML_NS = 'http://www.w3.org/1998/Math/MathML';

/** True for a javascript: URL, read the loose way browsers read it (tabs, newlines, any case). */
export function isJavascriptUrl(value: string): boolean {
  return /^javascript:/i.test(value.replace(/[\u0000-\u0020]/g, ''));
}

/** Largest SVG the tools accept. */
export const MAX_SVG_BYTES = 10 * 1024 * 1024;

export function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Same format as the rest of the site: 512 B, 4.2 KB, 38 KB, 1.25 MB. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** "−38%" style change from one size to another. */
export function formatChange(before: number, after: number): string {
  if (!before) return '0%';
  const pct = Math.round(((after - before) / before) * 100);
  if (pct === 0) return '0%';
  return pct < 0 ? `−${-pct}%` : `+${pct}%`;
}

/** Index range of the root <svg ...> start tag, skipping the XML declaration, comments and doctype. */
function findRootTag(text: string): { start: number; end: number } | null {
  let i = 0;
  while (i < text.length) {
    const lt = text.indexOf('<', i);
    if (lt === -1) return null;
    if (text.startsWith('<!--', lt)) {
      const close = text.indexOf('-->', lt + 4);
      if (close === -1) return null;
      i = close + 3;
      continue;
    }
    if (text.startsWith('<?', lt) || text.startsWith('<!', lt)) {
      const close = text.indexOf('>', lt);
      if (close === -1) return null;
      i = close + 1;
      continue;
    }
    if (!/^<svg[\s/>]/.test(text.slice(lt, lt + 5))) return null;
    // Find the end of the start tag, stepping over quoted attribute values.
    let quote: string | null = null;
    for (let j = lt + 4; j < text.length; j++) {
      const c = text[j];
      if (quote) {
        if (c === quote) quote = null;
      } else if (c === '"' || c === "'") {
        quote = c;
      } else if (c === '>') {
        return { start: lt, end: j + 1 };
      }
    }
    return null;
  }
  return null;
}

export interface NormalizedSvg {
  text: string;
  /** What was added, for the notes under the tool. */
  fixes: string[];
}

/**
 * Turn pasted markup into a standalone SVG file. SVG copied out of an HTML page usually has
 * no xmlns, and an <img> or a data URI shows nothing without it; xlink:href without an
 * xmlns:xlink declaration is a parse error in a file. Both declarations are added when missing.
 */
export function normalizeSvg(input: string): NormalizedSvg {
  const text = input.replace(/^\uFEFF/, '').trim();
  const fixes: string[] = [];
  const root = findRootTag(text);
  if (!root) return { text, fixes };
  const tag = text.slice(root.start, root.end);
  const additions: string[] = [];
  if (!/\sxmlns\s*=/.test(tag)) {
    additions.push(`xmlns="${SVG_NS}"`);
    fixes.push('Added xmlns="http://www.w3.org/2000/svg", which a standalone SVG file needs.');
  }
  if (/\sxlink:[a-z]+\s*=/i.test(text) && !/\sxmlns:xlink\s*=/.test(text)) {
    additions.push(`xmlns:xlink="${XLINK_NS}"`);
    fixes.push('Added the missing xmlns:xlink declaration for xlink:href.');
  }
  if (!additions.length) return { text, fixes };
  const fixed = `<svg ${additions.join(' ')}${tag.slice(4)}`;
  return { text: text.slice(0, root.start) + fixed + text.slice(root.end), fixes };
}

export type ParsedSvg = { ok: true; doc: Document; root: Element } | { ok: false; error: string };

/** Parse SVG text with the browser's XML parser. Never throws. */
export function parseSvg(text: string): ParsedSvg {
  if (!text.trim()) return { ok: false, error: 'Paste SVG code or drop an .svg file.' };
  if (typeof DOMParser === 'undefined') return { ok: false, error: 'This browser cannot read SVG.' };
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  } catch {
    return { ok: false, error: 'This is not valid SVG.' };
  }
  const parserError = doc.getElementsByTagName('parsererror')[0];
  if (parserError) {
    const detail = (parserError.textContent || '').match(/(line \d+[^\n]*?column \d+[^\n]*)/i);
    const message = detail ? detail[1].replace(/\s+/g, ' ').trim() : '';
    return { ok: false, error: message ? `This is not valid SVG: ${message}` : 'This is not valid SVG.' };
  }
  const root = doc.documentElement;
  if (!root || root.localName !== 'svg') {
    return { ok: false, error: 'The code needs an <svg> element at the top level.' };
  }
  return { ok: true, doc, root };
}

const UNIT_PX: Record<string, number> = { '': 1, px: 1, pt: 4 / 3, pc: 16, mm: 96 / 25.4, cm: 96 / 2.54, in: 96, em: 16, rem: 16 };

/** A width or height attribute in CSS px, or null for percentages, auto or garbage. */
export function lengthToPx(value: string | null): number | null {
  if (!value) return null;
  const m = value.trim().match(/^([+]?\d*\.?\d+(?:e[+-]?\d+)?)\s*([a-z]*)$/i);
  if (!m) return null;
  const factor = UNIT_PX[m[2].toLowerCase()];
  if (factor === undefined) return null;
  const px = parseFloat(m[1]) * factor;
  return px > 0 && Number.isFinite(px) ? px : null;
}

export function parseViewBox(value: string | null): [number, number, number, number] | null {
  if (!value) return null;
  const parts = value.trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n)) || parts[2] <= 0 || parts[3] <= 0) return null;
  return parts as [number, number, number, number];
}

export interface SvgInfo {
  /** Size the browser draws the SVG at when nothing else sets it, in CSS px. */
  width: number;
  height: number;
  viewBox: string | null;
  elements: number;
  paths: number;
  /** Things that would run or load if the SVG were put inline in a page. */
  risks: string[];
  /** How many of those are links to outside files (images, @import). */
  externalRefs: number;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function inspectSvg(root: Element): SvgInfo {
  const vb = parseViewBox(root.getAttribute('viewBox'));
  let width = lengthToPx(root.getAttribute('width'));
  let height = lengthToPx(root.getAttribute('height'));
  if (vb) {
    if (width && !height) height = (width * vb[3]) / vb[2];
    else if (height && !width) width = (height * vb[2]) / vb[3];
    else if (!width && !height) {
      width = vb[2];
      height = vb[3];
    }
  }
  const all = Array.from(root.getElementsByTagName('*'));
  let scripts = 0;
  let jsLinks = 0;
  let foreign = 0;
  let external = 0;
  const handlers = new Set<string>();
  for (const el of [root, ...all]) {
    const name = el.localName;
    if (name === 'script') scripts++;
    if (name === 'foreignObject') foreign++;
    for (const attr of Array.from(el.attributes)) {
      if (/^on/i.test(attr.localName) && !attr.prefix) handlers.add(attr.localName.toLowerCase());
      if (isJavascriptUrl(attr.value)) jsLinks++;
      else if ((attr.localName === 'href' || attr.localName === 'src') && name !== 'a' && /^\s*(https?:)?\/\//i.test(attr.value)) {
        external++;
      }
    }
    if (name === 'style' && /@import/i.test(el.textContent || '')) external++;
  }
  const risks: string[] = [];
  if (scripts) risks.push(plural(scripts, '<script> element'));
  if (handlers.size) risks.push(`${plural(handlers.size, 'event handler')} (${Array.from(handlers).join(', ')})`);
  if (jsLinks) risks.push(plural(jsLinks, 'javascript: URL'));
  if (foreign) risks.push(plural(foreign, '<foreignObject> element'));
  if (external) risks.push(plural(external, 'reference', 'references') + ' to outside files');
  return {
    width: width || 300,
    height: height || 150,
    viewBox: root.getAttribute('viewBox'),
    elements: all.length + 1,
    paths: all.filter((el) => el.localName === 'path').length,
    risks,
    externalRefs: external,
  };
}

/** Gzipped size in bytes, or null where the browser has no CompressionStream. */
export async function gzipSize(text: string): Promise<number | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    return buf.byteLength;
  } catch {
    return null;
  }
}

/** A data URL for showing the SVG in an <img>. As an image it cannot run scripts or load files. */
export function svgImageSrc(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Suggested file stem from a dropped file's name. */
export function fileStem(name: string): string {
  return name.replace(/\.svgz?$/i, '').replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'image';
}
